import {
  FulfillSuccess,
  Product,
  PurchaseError,
} from "@b2bportal/purchase-api";
import {
  CardPaymentSelectors,
  CartFulfillErrorModal,
  LodgingSelectors,
  ParentState,
  RewardsPaymentSelectors,
  WalletSelectors,
  commonSelectors,
} from "@capone/checkout";
import {
  getBookedAirlineCodes,
  getGlobalTrackingProperties,
  getMatchedBusinessLoyaltyPrograms,
  hasRelevantFrequentFlyer,
  isCorpTenant,
} from "@capone/common";
import * as H from "history";
import {
  COMPLETE_BUY_PACKAGE,
  CorpSessionInfo,
  FiatPrice,
  IPerson,
  RewardsAccount,
  RewardsPrice,
} from "redmond";
import { DoneInvokeEvent, StateValue, send as xstateSend } from "xstate";
import dayjs from "dayjs";
import queryStringParser from "query-string";
import { config } from "../../../api/config";
import { Event } from "./events";
import { getUserSelectedRecommendedFlight } from "./selectors/common";
import { PackagesMachineContext } from "./types";
import { getLoyaltyNumberForChosenProduct } from "../components/capone-corporate/LoyaltyInformationForm/component";

export const stringifyParentStateValue = (state: StateValue) => {
  if (typeof state === "string") return state;
  if (typeof state === "object") {
    const key = Object.keys(state)[0];
    return key;
  }
  return "";
};

export const handleFulfillSuccess = (history: H.History) => {
  history.push("/");
};

export const validateContext = (_: PackagesMachineContext) => true;
// Boolean(
//   (ctx.flightShop.shopPricingInfo.fare?.length || -1) > 0 &&
//     ctx.flightSearch.departureDate &&
//     ctx.flightSearch.destination &&
//     ctx.flightSearch.origin &&
//     ctx.flightShop.selectedTrip.outgoingFareId &&
//     ctx.flightShop.selectedTrip.outgoingSliceId &&
//     ctx.flightShop.selectedTrip.tripId &&
//     Object.keys(ctx.flightShop.airports || {}).length > 0
// );

export function integerWithCommas(x: number) {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

export function numberWithCommas(x: number) {
  return (
    integerWithCommas(Math.floor(x)) +
    (x % 1 ? `.${Math.round((x % 1) * 100)}` : "")
  );
}

export const formatCurrency = (price: FiatPrice, discount?: boolean) =>
  Intl.NumberFormat("en-US", {
    style: "currency",
    currency: price.currencyCode,
    currencyDisplay: "narrowSymbol",
  }).format(discount ? Math.abs(price.value) * -1 : price.value);

export const formatRewards = (price: RewardsPrice, discount?: boolean) => {
  const priceValueToUse = discount ? Math.abs(price.value) : price.value;

  const isCash = price.currencyDescription?.includes("cash");

  const currencySymbol = isCash ? "$" : "";
  return `${
    discount ? "-" : ""
  }${currencySymbol}${priceValueToUse.toLocaleString("en-US", {
    minimumFractionDigits: isCash ? 2 : 0,
    maximumFractionDigits: isCash ? 2 : 0,
  })} ${price?.currencyDescription}`;
};

export const getRewardsAmountFromFiat = (
  usdAmount: number,
  rewardsAccount: RewardsAccount
): RewardsPrice => {
  const amountToUse =
    usdAmount *
    (rewardsAccount.rewardsBalance.currencyDescription?.includes("cash")
      ? 1
      : 100);

  return {
    ...rewardsAccount.rewardsBalance,
    value: amountToUse,
  };
};

export const getCorporatePollCompleteProperties = (
  context: PackagesMachineContext
) => {
  const selectedProduct = LodgingSelectors.getSelectedRoomProduct({
    context: context as any,
  });

  const loyaltyProgramCode =
    (selectedProduct &&
      "loyaltyProgramCode" in selectedProduct &&
      selectedProduct.loyaltyProgramCode) ||
    undefined;

  const selectedCorpTravelers =
    context.passengerInformation.userSelectedTravelersList;

  const sessionInfo = commonSelectors.getSessionInfo({
    context: context as any,
  });

  const globalProperties = {
    sessionInfo: sessionInfo as CorpSessionInfo,
    corpBusinessAccounts: context.corpBusinessAccounts || [],
    rewardsAccounts: context.rewardsPayment.rewardsAccounts || [],
    selectedAccountReferenceId:
      context.selectedAccount?.accountReferenceId || null,
    policyTier: context.policyTier,
    modalMetadata: context.modalMetadata,
  };

  const globalTrackingProperties = getGlobalTrackingProperties(
    "capone-corporate",
    globalProperties
  );

  const isSelfBooked = selectedCorpTravelers.some(
    (passenger: IPerson) =>
      passenger.givenName === sessionInfo?.userInfo.firstName &&
      passenger.surname === sessionInfo?.userInfo.lastName
  );

  const hotelLoyaltyAdded = selectedCorpTravelers.some((guest) =>
    getLoyaltyNumberForChosenProduct(loyaltyProgramCode, guest)
  );

  // Count travelers (not FF numbers) who have loyalty for booked airlines
  const bookedAirlineCodes = getBookedAirlineCodes(context.flightShop.tripDetails);
  const frequentFlyerAddedCount = selectedCorpTravelers.filter((passenger) =>
    hasRelevantFrequentFlyer(passenger, bookedAirlineCodes)
  ).length;

  const corpLoyaltyPrograms =
    sessionInfo &&
    "corporateInfo" in sessionInfo
      ? sessionInfo.corporateInfo.businessLoyaltyPrograms
      : undefined;

  const businessLoyaltyPrograms = corpLoyaltyPrograms?.join(", ") || undefined;

  const matchedBusinessLoyaltyPrograms = getMatchedBusinessLoyaltyPrograms(
    corpLoyaltyPrograms,
    bookedAirlineCodes
  );

  return {
    ...globalTrackingProperties,
    purpose_of_travel: context.tripPurpose,
    booked_self_name_match: isSelfBooked,
    total_guests_added: selectedCorpTravelers.length,
    loyalty_added_count: hotelLoyaltyAdded ? 1 : 0,
    x_price_difference:
      (context.cartQuote.priceChange?.difference ?? 0) *
      (context.cartQuote.priceChange?.isIncrease ? 1 : -1),
    business_loyalty_programs: businessLoyaltyPrograms,
    matched_business_loyalty_programs:
      matchedBusinessLoyaltyPrograms.join(", ") || undefined,
    hotel_loyalty_eligible: Boolean(loyaltyProgramCode),
    added_hotel_loyalty: hotelLoyaltyAdded,
    frequent_flyer_added_count: frequentFlyerAddedCount,
  };
};

export const trackPollCompleteProperties = {
  eventName: COMPLETE_BUY_PACKAGE,
  // trackProperties returned in pollfulfill response will be also added

  getAdditionalProperties: (
    context: PackagesMachineContext,
    event: DoneInvokeEvent<
      | FulfillSuccess
      | {
          data: Array<PurchaseError>;
          type: CartFulfillErrorModal;
        }
    >
  ) => {
    let reservationId;
    if ("products" in event.data) {
      const packageProduct = event.data.products.find(
        (product) => product.type === Product.Package
      );
      reservationId = packageProduct?.value?.customerReservationId;
    }

    return {
      reservation_id: reservationId,
      total_card_amount_usd: CardPaymentSelectors.getTotalCardPaymentRequired({
        context,
      }),
      card_product_used: CardPaymentSelectors.getSelectedPaymentCardType({
        context,
      }),
      total_rewards_amount_usd:
        RewardsPaymentSelectors.getRewardsFiatAmountToApply({
          context,
        })?.value || 0,

      credit_amt_redeemed: Math.abs(
        WalletSelectors.getTravelWalletCreditToApply({ context })?.amount
          .amount || 0
      ),
      offer_amount_redeemed_usd: Math.abs(
        WalletSelectors.getSelectedOffer({ context })?.amount.amount || 0
      ),
      credit_redeemed: !!WalletSelectors.getTravelWalletCreditToApply({
        context,
      })?.amount.amount,
      offer_used: !!context.wallet.selectedOfferId,
      package_discount_usd: context.redeemedPackageDiscountAmount,
      package_discount_used: !!context.redeemedPackageDiscountAmount,
      recommended_flight_selected: getUserSelectedRecommendedFlight({
        context,
      }),
      lodging_row_index: context.lodgingShop.selectedLodgingIndex,
      account_type_selected: context.selectedAccount?.productDisplayName ?? "",
      entry_point: context.entryPoint,
      top_booked: context.selectedLodgingIsTopSeller || false,
      meal_plan: context.selectedLodgingMealPlan,
      searched_hotel: context.selectedLodgingIsSearched || false,
      selected_from_map: context.lodgingSelectedFromMap || false,
      booking_duration: dayjs().diff(context.initialTime, "seconds", true),
      ...WalletSelectors.getSelectedOffer({ context })?.trackingPropertiesV2
        ?.properties,
      ...WalletSelectors.getTravelWalletCreditToApply({ context })
        ?.trackingPropertiesV2?.properties,
      ...(isCorpTenant(config.TENANT) &&
        getCorporatePollCompleteProperties(context)),
    };
  },

  getAdditionalEncryptedProperties: (context: PackagesMachineContext) => {
    const encryptedProperties: string[] = [];

    const lodgingTrackingProperties =
      LodgingSelectors.getLodgingFulfillTrackingProperties({
        context: context as any,
      });

    const selectedOffer = WalletSelectors.getSelectedOffer({ context });

    if (lodgingTrackingProperties.encryptedProperties) {
      encryptedProperties.push(lodgingTrackingProperties.encryptedProperties);
    }

    if (selectedOffer?.trackingPropertiesV2?.encryptedProperties) {
      encryptedProperties.push(
        selectedOffer.trackingPropertiesV2.encryptedProperties
      );
    }

    return encryptedProperties;
  },
};

const untrackedStates = [
  ParentState.loading,
  ParentState.cartQuote,
  ParentState.cartUpdate,
  ParentState.cartFulfill,
];

// Map of states that should not transition when query params change
// Map: { state that should not transition => previous tracked state }
// Eg: if user is in "cartQuote" and hits back on their browser, the user should not be allowed to go back 1 step and
// instead stay in the current step till it resolves
const preventTransitionStates = {
  [ParentState.cartQuote]: ParentState.passengerInformation,
  [ParentState.cartUpdate]: ParentState.passengerInformation,
  [ParentState.cartFulfill]: ParentState.cardPayment,
};

export const mapFlightStateToTransitionEvent: Record<string, Event> = {
  [ParentState.passengerInformation]: Event.GO_TO_PASSENGER_SELECT,
  [ParentState.lodgingPassengerInformation]: Event.GO_TO_HOTEL_PASSENGER_SELECT,
  [ParentState.contactInformation]: Event.GO_TO_CONTACT_INFORMATION,
  [ParentState.seatSelection]: Event.GO_TO_SEAT_SELECTION,
  [ParentState.cardPayment]: Event.GO_TO_CARD_PAYMENT,
  [ParentState.review]: Event.GO_TO_REVIEW,
  [ParentState.bookingConfirmation]: Event.GO_TO_REVIEW,
};

enum MobileSteps {
  passengerInformation,
  lodgingPassengerInformation,
  contactInformation,
  seatSelection,
  cardPayment,
  review,
  bookingConfirmation,
}
export const mapStepToNumberString = {
  passengerInformation: "1",
  lodgingPassengerInformation: "2",
  contactInformation: "3",
  seatSelection: "4",
  cardPayment: "5",
  review: "6",
  bookingConfirmation: "7",
};

export const populateCheckoutQueryParams = (
  state: StateValue,
  history: H.History
) => {
  const queryString = history?.location?.search || "";
  const parsedQuery = queryStringParser.parse(queryString);
  const stateQueryParam = parsedQuery?.bookStep;

  const stringifiedNewState = stringifyParentStateValue(state);

  const shouldUpdateQuery = !untrackedStates.includes(
    stringifiedNewState as ParentState
  );

  // `bookstep` is a number to match the other funnels not on checkout li
  const bookStepInString = MobileSteps[Number(stateQueryParam) - 1];

  //  If xstate state value is not in query params or if it conflicts with what the
  //  current query params contains, then we push new query params to the URL

  // const bookStepInNumber =
  //   stringifiedNewState in mapStepToNumberString
  //     ?  MobileSteps[stringifiedNewState]
  //     : undefined;

  const historyData = {
    pathname: history.location.pathname,
    search: queryStringParser.stringify({
      ...parsedQuery,
      bookStep: mapStepToNumberString[stringifiedNewState],
    }),
    state: history.location.state,
  };

  if (!stateQueryParam && shouldUpdateQuery) {
    history.replace(historyData);
  } else if (
    stateQueryParam &&
    bookStepInString &&
    stringifiedNewState !== bookStepInString &&
    shouldUpdateQuery
  ) {
    history.push(historyData);
  }
};

export const transitionStateOnPathnameChange = (
  state: StateValue,
  history: H.History,
  send: typeof xstateSend
) => {
  const queryString = history.location.search;
  const parsedQuery = queryStringParser.parse(queryString);
  const stateQueryParam = parsedQuery?.bookStep;

  const stringifiedNewState = stringifyParentStateValue(state);

  const preventTransition = !!preventTransitionStates[stringifiedNewState];

  const bookStepInString = MobileSteps[Number(stateQueryParam) - 1];

  const allowTransition =
    !preventTransition &&
    stateQueryParam &&
    bookStepInString &&
    !untrackedStates.includes(bookStepInString as ParentState);

  const hasSucceededFulfillment =
    stringifiedNewState === ParentState.bookingConfirmation;

  if (
    stateQueryParam &&
    bookStepInString &&
    stringifiedNewState !== bookStepInString &&
    allowTransition
  ) {
    if (hasSucceededFulfillment) {
      history.push("/");
    } else {
      send({
        type: mapFlightStateToTransitionEvent[bookStepInString as ParentState],
      });
    }
  } else if (
    preventTransition &&
    bookStepInString &&
    preventTransitionStates[stringifiedNewState] !== bookStepInString
  ) {
    const historyData = {
      pathname: history.location.pathname,
      search: queryStringParser.stringify({
        ...parsedQuery,
        bookStep:
          mapStepToNumberString[preventTransitionStates[stringifiedNewState]],
      }),
      state: history.location.state,
    };
    // Pushing the same stack that was removed when the user goes 1 step back in their browser
    history.push(historyData);
  }
};
